/**
 * components/platform/billingEmbed.tsx
 * This console mounts the plan card and opens the billing drawer exactly as
 * every other product does (CC-05 §2): through loader.js and a short-lived
 * embed token. There is no second plan/upgrade/top-up UI anywhere in Platform.
 */

import React, { useEffect, useRef, useState } from 'react';
import { View, Text, Platform } from 'react-native';

import { useTheme } from '../../context/themeContext';
import { createEmbedToken } from '../../services/platform/platformApi';
import { Platform_Embed_Host } from '../../api-endpoints/api-endpoints';
import { FS } from '../../utils/platformTokens';

type Trigger = { kind: 'manual' } | { kind: 'limit_hit'; unitCode: string } | { kind: 'feature_locked'; featureCode: string };
type Opts = { product: string; host: string; getToken: () => Promise<string>; theme: 'light' | 'dark' };
interface SKBillingApi {
  mountCard: (el: HTMLElement, opts: Opts) => void;
  openDrawer: (opts: Opts, intent: 'upgrade' | 'topup', trigger?: Trigger, extra?: { request?: string }) => void;
}

// Same build as this console, so the loader is served from our own origin.
const LOADER_PATH = '/embed/loader.js';
let loading: Promise<SKBillingApi> | null = null;

export function loadSKBilling(): Promise<SKBillingApi> {
  const w = window as unknown as { SKBilling?: SKBillingApi };
  if (w.SKBilling) return Promise.resolve(w.SKBilling);
  if (!loading) {
    loading = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = LOADER_PATH;
      s.async = true;
      s.onload = () => (w.SKBilling ? resolve(w.SKBilling) : reject(new Error('SKBilling missing')));
      s.onerror = () => { loading = null; reject(new Error('loader.js failed to load')); };
      document.head.appendChild(s);
    });
  }
  return loading;
}

export const embedOpts = (product: string, isDark: boolean): Opts => ({
  product,
  host: Platform_Embed_Host,
  getToken: () => createEmbedToken(product),
  theme: isDark ? 'dark' : 'light',
});

export function openBillingDrawer(product: string, isDark: boolean, intent: 'upgrade' | 'topup', extra?: { request?: string }) {
  return loadSKBilling().then((sk) => sk.openDrawer(embedOpts(product, isDark), intent, { kind: 'manual' }, extra));
}

/** The plan card for one product. Web only — the native app opens billing on the web. */
export const PlanCard: React.FC<{ product: string }> = ({ product }) => {
  const { theme, isDark } = useTheme();
  const ref = useRef<View>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (Platform.OS !== 'web') return;
    let alive = true;
    loadSKBilling()
      .then((sk) => { if (alive && ref.current) sk.mountCard(ref.current as unknown as HTMLElement, embedOpts(product, isDark)); })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [product, isDark]);

  if (Platform.OS !== 'web') {
    return <Text style={{ color: theme.colors.ink3, fontSize: FS.md }}>Open Settings on the web to change plans or top up.</Text>;
  }
  if (failed) {
    return <Text style={{ color: theme.colors.danger, fontSize: FS.md }}>Couldn't load plan details. Reload the page to try again.</Text>;
  }
  return <View ref={ref} style={{ width: '100%', minHeight: 120 }} />;
};
